import { ArrowBack, Add, Delete } from '@mui/icons-material';
import { Box, Typography, Grid, IconButton, Card, CardContent, Chip } from '@mui/material';
import Button from "themeComponents/Button";
import { Save } from '@mui/icons-material';
import FileUpload from 'components/FileUpload';
import FormikInput from 'components/formikInput';
import { Formik, Form } from 'formik';
import { useCourse } from 'hooks/useCourse';
import useIndustry from 'hooks/useIndustry';
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom';
import Footer from 'themeComponents/Footer';
import DashboardLayout from "themeComponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from 'themeComponents/Navbars/DashboardNavbar';
import * as Yup from 'yup';
import { UUID } from 'crypto';
import useAuth from 'hooks/useAuth';
import useCoursePermissions from 'hooks/useCoursePermissions';
import { AppContext } from 'context/appContext';
type CourseData = {
  id?: UUID;
  title: string;
  description: string;
  status: string;
  price: number;
  isFree: boolean;
  quotaPricing: Record<string, number>;
  categoryIDs: string[];
  thumbnailUrl: string;
  courseLanguages?: string[];
}

type QuotaTier = {
  seats: string;
  price: string;
};

type CourseFormValues = CourseData & {
  customQuotas: QuotaTier[];
};
function Course(): React.JSX.Element {
  const navigate = useNavigate();
  const {
    user,
    isCompany,
    isCreator,
    isAdmin,
    isCompanyAdmin,
    isFellowAdmin,
    isManager,
    isHR,
    isEmployee,
    isSupervisor,
    isRestrictedEmployee,
  } = useAuth();
  const { canCreateCourse, companyOwnerIdForCreate } = useCoursePermissions({
    user,
    isAdmin,
    isCompany,
    isCompanyAdmin,
    isFellowAdmin,
    isCreator,
    isManager,
    isHR,
    isEmployee,
    isSupervisor,
    isRestrictedEmployee,
  });
  const { showAlert } = React.useContext(AppContext);
  const { id: courseId } = useParams();
  let isUpdate = false;
  if (courseId != null) {
    isUpdate = true;
  }
  const { industries, getAllIndustries } = useIndustry();
  const { addCourse, getCourse, editCourse } = useCourse();
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<CourseData>({
    title: '',
    description: '',
    status: 'draft',
    price: 0,
    isFree: false,
    thumbnailUrl: '',
    quotaPricing: {},
    categoryIDs: [],
  })

  const handleFinalSubmit = async (values: CourseFormValues) => {
    setLoading(true);
    // Handle final submission of course data
    try {
      const normalizedQuotaPricing: Record<string, number> = {};
      const customQuotas = Array.isArray(values.customQuotas) ? values.customQuotas : [];
      for (const row of customQuotas) {
        const seats = parseInt(String(row?.seats ?? ""), 10);
        const tierPrice = parseFloat(String(row?.price ?? "")) || 0;
        if (Number.isInteger(seats) && seats > 0 && tierPrice > 0) {
          normalizedQuotaPricing[String(seats)] = tierPrice;
        }
      }
      const normalizedCategoryIDs = Array.isArray(values.categoryIDs) ? values.categoryIDs : [];
      const normalizedCourseLanguages = Array.isArray(values.courseLanguages)
        ? values.courseLanguages
        : [];

      const courseData: any = {
        title: values.title,
        description: values.description,
        status: values.status,
        price: values.price,
        isFree: Boolean(values.isFree),
        quotaPricing: Object.keys(normalizedQuotaPricing).length > 0 ? normalizedQuotaPricing : undefined,
        categoryIDs: normalizedCategoryIDs.length > 0 ? normalizedCategoryIDs : [], // Always send as array
        thumbnailUrl: values.thumbnailUrl,
        courseLanguages: normalizedCourseLanguages.length > 0 ? normalizedCourseLanguages : undefined,
        companyID: companyOwnerIdForCreate || null,
        instructorID: isCreator && !companyOwnerIdForCreate ? user?._id : null,
        instructorName: isCreator ? `${user?.firstname} ${user?.lastname}` : null,
      };
      if (isUpdate && courseId != null) {
        const course = await editCourse(courseId, courseData);
        if (course.status) {
          setData(course.data);
          // navigate(`/courses/create?id=${courseId}`);
          navigate(`/courses/${courseId}/modules`);
        } else {
          showAlert(course.error, "error", "Error");
        }
      } else {
        const course = await addCourse(courseData);
        if (course.status) {
          setData(course.data);
          // navigate(`/courses/create?id=${course.data.id}`);
          navigate(`/courses/${course.data.id}/modules`);
        } else {
          showAlert(course.error, "error", "Error");
        }
      }
    } catch (error) {
      showAlert("An error occurred while saving the course.", "error", "Error");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    if (!isUpdate && !canCreateCourse) {
      showAlert("You do not have permission to create courses.", "error", "Access denied");
      navigate("/courses");
    }
  }, [isUpdate, canCreateCourse, navigate, showAlert]);

  useEffect(() => {
    if (courseId != null) {
      getCourse(courseId).then((courseData) => {
        // Map thumbnailURL (from backend) to thumbnailUrl (form state)
        const mappedData = {
          ...courseData.data,
          thumbnailUrl: courseData.data.thumbnailUrl || courseData.data.thumbnailURL || '',
          quotaPricing:
            courseData.data.quotaPricing && typeof courseData.data.quotaPricing === 'object'
              ? courseData.data.quotaPricing
              : {},
          categoryIDs: Array.isArray(courseData.data.categoryIDs) ? courseData.data.categoryIDs : [],
          courseLanguages: Array.isArray(courseData.data.courseLanguages)
            ? courseData.data.courseLanguages
            : [],
          description: courseData.data.description || '',
        };
        setData(mappedData);
      });
    }
    // Fetch industries/categories on mount
  }, [courseId]);
  useEffect(() => {
    // Fetch industries/categories on mount
    getAllIndustries();
  }, []);

  const initialCustomQuotas: QuotaTier[] = Object.entries(data.quotaPricing || {})
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([seats, tierPrice]) => ({
      seats: String(seats),
      price: String(tierPrice),
    }));

  if (initialCustomQuotas.length === 0) {
    initialCustomQuotas.push({ seats: "", price: "" });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box py={3}>
        <Box mb={3} display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate("/courses")}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {isUpdate ? "Edit Course" : "Create Course"}
            </Typography>
            <Box mt={0.5} display="flex" gap={1} flexWrap="wrap">
              <Chip size="small" color="info" variant="outlined" label="Step 1 of 3" />
              <Chip size="small" color="secondary" variant="outlined" label="Course details" />
            </Box>
          </Box>
        </Box>
        <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
          <CardContent>
            <Formik
              key={'create'}
              enableReinitialize={true}
              initialValues={{
                title: data.title,
                description: data.description,
                status: data.status,
                price: data.price,
                isFree: data.isFree,
                quotaPricing: data.quotaPricing,
                customQuotas: initialCustomQuotas,
                categoryIDs: data.categoryIDs,
                thumbnailUrl: data.thumbnailUrl,
                thumbnailFile: null,
                courseLanguages: data.courseLanguages || [],
              }}
              validationSchema={Yup.object({
                title: Yup.string().required("Title is required"),
                description: Yup.string().optional(),
              })}
              onSubmit={handleFinalSubmit}
            >
              {({ values, setFieldValue }) => (
                <Form>
                  <Box>
                    <Typography variant="h6" mb={3} fontWeight="bold">
                      Course Information
                    </Typography>
                    <Typography variant="body2" color="text" mb={3}>
                      Start by providing information about your course. You&apos;ll add modules and content in the next steps.
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <FormikInput
                          name="title"
                          label="Course Title *"
                          placeholder="e.g., Introduction to React"
                          size="large"
                        />
                        <Typography variant="caption" color="text" mt={0.5} display="block">
                          Enter a clear and descriptive title for your course
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <FormikInput
                          name="description"
                          label="Course Description"
                          placeholder="Describe what students will learn in this course..."
                          multiline
                          rows={4}
                          size="large"
                        />
                        <Typography variant="caption" color="text" mt={0.5} display="block">
                          Provide a detailed description of the course content and learning objectives
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormikInput
                          name="status"
                          type="dropdown"
                          label="Course Status *"
                          options={[
                            { value: "draft", label: "Draft (Not visible to students)" },
                            { value: "published", label: "Published (Visible to students)" },
                          ]}
                          size="large"
                        />
                        <Typography variant="caption" color="text" mt={0.5} display="block">
                          Draft courses are only visible to you. Published courses are available to students.
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box mb={2}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FormikInput
                              name="thumbnailUrl"
                              label="Or Enter Thumbnail URL"
                              placeholder="https://example.com/thumbnail.jpg"
                              size="medium"
                            />
                            <FileUpload
                              type="image"
                              onUploadComplete={(url) => {
                                if (typeof url === 'string') {
                                  setFieldValue('thumbnailUrl', url);
                                }
                              }}
                              existingUrl={data.thumbnailUrl}
                            // label="Upload Thumbnail Image"
                            />

                          </Box>
                          <Typography variant="caption" color="text" mt={0} display="block">
                            Upload an image file or enter a URL for the course thumbnail (optional)
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormikInput
                          name="categoryIDs"
                          type="multiselect-dropdown"
                          label="Categories *"
                          options={industries.map(item => ({ value: item._id, label: item.name }))}
                          size="small"
                        />
                        <Typography variant="caption" color="text" mt={1} display="block">
                          Select one or more categories for this course
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormikInput
                          name="courseLanguages"
                          type="multiselect-dropdown"
                          label="Language *"
                          options={[
                            { value: "English", label: "English" },
                            { value: "Spanish", label: "Spanish" },
                            { value: "French", label: "French" },
                            { value: "German", label: "German" },
                            { value: "Arabic", label: "Arabic" },
                          ]}
                          size="small"
                        />
                        <Typography variant="caption" color="text" mt={0.5} display="block">
                          Select the languages in which this course is offered
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormikInput
                          name="price"
                          type="number"
                          label="Single Course Price ($) *"
                          placeholder="0.00"
                          size="large"
                        />
                        <Typography variant="caption" color="text" mt={0.5} display="block">
                          Price for a single course purchase by individual users
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormikInput
                          name="isFree"
                          type="checkbox"
                          label="Mark as Free Course"
                          size="large"
                        />
                        <Typography variant="caption" color="text" mt={0.5} display="block">
                          Free courses are available to all users without payment. Overrides all pricing.
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" fontWeight="medium" mb={2}>
                          Quota-Based Pricing (for Companies)
                        </Typography>
                        <Typography variant="body2" color="text" mb={2}>
                          Add as many custom quota tiers as you want. For each tier, set total seats and total price.
                        </Typography>
                        <Box display="flex" flexDirection="column" gap={2}>
                          {(values.customQuotas || [{ seats: "", price: "" }]).map((row: QuotaTier, index: number) => (
                            <Grid container spacing={2} key={`quota-row-${index}`} alignItems="center">
                              <Grid item xs={12} md={4}>
                                <FormikInput
                                  name={`customQuotas.${index}.seats`}
                                  type="number"
                                  label="Seats"
                                  placeholder="e.g. 25"
                                  size="large"
                                />
                              </Grid>
                              <Grid item xs={12} md={5}>
                                <FormikInput
                                  name={`customQuotas.${index}.price`}
                                  type="number"
                                  label="Total Price ($)"
                                  placeholder="e.g. 900"
                                  size="large"
                                />
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  onClick={() => {
                                    const nextRows = [...(values.customQuotas || [])];
                                    nextRows.splice(index, 1);
                                    setFieldValue("customQuotas", nextRows.length > 0 ? nextRows : [{ seats: "", price: "" }]);
                                  }}
                                >
                                  <Delete fontSize="small" sx={{ mr: 0.5 }} /> Remove
                                </Button>
                              </Grid>
                            </Grid>
                          ))}

                          <Box>
                            <Button
                              variant="outlined"
                              color="info"
                              size="small"
                              onClick={() => {
                                setFieldValue("customQuotas", [
                                  ...(values.customQuotas || []),
                                  { seats: "", price: "" },
                                ]);
                              }}
                            >
                              <Add fontSize="small" sx={{ mr: 0.5 }} /> Add Quota Tier
                            </Button>
                          </Box>
                        </Box>
                        <Typography variant="caption" color="text" mt={1} display="block">
                          For seat counts not listed in these tiers, pricing falls back to base course price × requested seats.
                        </Typography>
                      </Grid>

                    </Grid>
                  </Box>
                  <Box mt={4} display="flex" justifyContent="flex-end">
                    <Button
                      type="button"
                      variant="gradient"
                      color="info"
                      loading={loading}
                      onClick={async (e) => {
                        e.preventDefault();
                        await handleFinalSubmit(values);
                      }}
                    >
                      <Save sx={{ mr: 1 }} /> {"Save & Continue"}
                    </Button>
                  </Box>
                </Form>
              )}
            </Formik>
          </CardContent>
        </Card>
      </Box>
      <Footer />
    </DashboardLayout>
  )
}
export default Course;